import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { startToolOperation, trackToolRejection, type ToolAnalyticsContext, type ToolOperation } from '@saas-pages/shared/public/runtime'
import { Button, Card, CardContent } from '@saas-pages/shared/ui'
import { createWorkerError, type WorkerErrorPayload } from '@saas-pages/shared/worker'
import { useWorkerJob } from '@saas-pages/shared/worker/react'
import {
  MAX_AUDIO_BYTES,
  isAudioTrimWorkerResult,
  validateRange,
  validateSource,
  type AudioMetadata,
  type AudioTrimFailureCode,
  type AudioTrimWorkerInput,
  type AudioTrimWorkerResult,
} from '@/features/audio-trimmer/model/audioTrim'

type JobContext = { kind: 'inspect' | 'trim'; generation: number; startedAt: number }

export const AUDIO_TRIM_WORKER_TIMEOUT_MS = 30_000

export function AudioTrimmer({ locale }: { locale: 'ru' | 'en' }) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const resultRef = useRef<HTMLDivElement | null>(null)
  const sourceUrlRef = useRef<string | null>(null)
  const resultUrlRef = useRef<string | null>(null)
  const generationRef = useRef(0)
  const jobRef = useRef<JobContext | null>(null)
  const operationRef = useRef<ToolOperation | null>(null)
  const trimLockRef = useRef(false)
  const previewRejectedRef = useRef(false)
  const [file, setFile] = useState<File | null>(null)
  const [sourceUrl, setSourceUrl] = useState<string | null>(null)
  const [resultUrl, setResultUrl] = useState<string | null>(null)
  const [metadata, setMetadata] = useState<AudioMetadata | null>(null)
  const [startSeconds, setStartSeconds] = useState(0)
  const [endSeconds, setEndSeconds] = useState(0)
  const [snappedRange, setSnappedRange] = useState<{ start: number; end: number } | null>(null)
  const [error, setError] = useState<AudioTrimFailureCode | 'cancelled' | null>(null)

  const analytics = useMemo<ToolAnalyticsContext>(() => ({
    app: 'mediatik',
    domain: 'mediatik.ru',
    route: '/obrezat-audio',
    utility: 'audio-trimmer',
    processingMode: 'local',
    inputMethod: 'upload',
    locale,
    landingLocale: locale,
  }), [locale])

  const workerJob = useWorkerJob<AudioTrimWorkerInput, AudioTrimWorkerResult>({
    timeoutMs: AUDIO_TRIM_WORKER_TIMEOUT_MS,
    timeoutCode: 'processing_failed',
    createWorker: () => new Worker(new URL('../model/audioTrim.worker.ts', import.meta.url), { type: 'module' }),
    validate: (input) => {
      try {
        validateSource(input.file)
        if (input.action === 'trim') validateRange(input.startSeconds, input.endSeconds, metadata?.durationSeconds ?? 0)
      } catch (cause) {
        const code = cause instanceof Error && isFailureCode(cause.message) ? cause.message : 'invalid_input'
        throw createWorkerError('recoverable', code)
      }
    },
    validateResult: isAudioTrimWorkerResult,
    onSuccess: (result) => {
      const context = jobRef.current
      if (!context || context.generation !== generationRef.current || context.kind !== result.action) return
      if (result.action === 'inspect') {
        setMetadata(result.metadata)
        setStartSeconds(0)
        setEndSeconds(roundTime(result.metadata.durationSeconds))
        setError(null)
      } else {
        revokeResult()
        const nextUrl = URL.createObjectURL(result.result)
        resultUrlRef.current = nextUrl
        setResultUrl(nextUrl)
        setSnappedRange({ start: result.snappedStartSeconds, end: result.snappedEndSeconds })
        setError(null)
        operationRef.current?.success(performance.now() - context.startedAt)
        operationRef.current = null
        trimLockRef.current = false
      }
      jobRef.current = null
    },
    onError: (workerError) => {
      const context = jobRef.current
      if (!context || context.generation !== generationRef.current) return
      const code = toFailureCode(workerError)
      setError(code)
      if (context.kind === 'trim') {
        operationRef.current?.error(code)
        operationRef.current = null
        trimLockRef.current = false
      } else {
        trackToolRejection(analytics, code)
      }
      jobRef.current = null
    },
    onCancel: () => {
      const context = jobRef.current
      if (!context || context.generation !== generationRef.current) return
      if (context.kind === 'trim') {
        operationRef.current?.error('cancelled')
        operationRef.current = null
        trimLockRef.current = false
        setError('cancelled')
      }
      jobRef.current = null
    },
  })

  useEffect(() => () => {
    generationRef.current += 1
    jobRef.current = null
    operationRef.current?.error('cancelled')
    operationRef.current = null
    trimLockRef.current = false
    if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current)
    if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current)
  }, [])

  useEffect(() => {
    if (!resultUrl) return
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    resultRef.current?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest' })
  }, [resultUrl])

  const applyFile = useCallback((files: FileList | null) => {
    const nextGeneration = generationRef.current + 1
    generationRef.current = nextGeneration
    jobRef.current = null
    operationRef.current?.error('cancelled')
    operationRef.current = null
    trimLockRef.current = false
    workerJob.reset()
    revokeSource()
    revokeResult()
    setMetadata(null)
    setSnappedRange(null)
    setError(null)
    previewRejectedRef.current = false
    if (!files || files.length !== 1) {
      setFile(null)
      setError('invalid_input')
      trackToolRejection(analytics, 'invalid_input')
      return
    }
    const nextFile = files[0]!
    try {
      validateSource(nextFile)
    } catch (cause) {
      const code: AudioTrimFailureCode = nextFile.size > MAX_AUDIO_BYTES ? 'size_limit' : 'invalid_input'
      setFile(null)
      setError(code)
      trackToolRejection(analytics, code)
      return
    }
    const nextUrl = URL.createObjectURL(nextFile)
    sourceUrlRef.current = nextUrl
    setSourceUrl(nextUrl)
    setFile(nextFile)
    jobRef.current = { kind: 'inspect', generation: nextGeneration, startedAt: performance.now() }
    void workerJob.run({ action: 'inspect', file: nextFile })
  }, [analytics, workerJob])

  const startTrim = useCallback(() => {
    if (!file || !metadata || trimLockRef.current || workerJob.state.status === 'running' || workerJob.state.status === 'validating') return
    try {
      validateRange(startSeconds, endSeconds, metadata.durationSeconds)
    } catch {
      setError('invalid_input')
      trackToolRejection(analytics, 'invalid_input')
      return
    }
    revokeResult()
    setSnappedRange(null)
    setError(null)
    trimLockRef.current = true
    const generation = generationRef.current
    jobRef.current = { kind: 'trim', generation, startedAt: performance.now() }
    operationRef.current = startToolOperation(analytics)
    void workerJob.run({ action: 'trim', file, startSeconds, endSeconds })
  }, [analytics, endSeconds, file, metadata, startSeconds, workerJob])

  const cancel = useCallback(() => workerJob.cancel(), [workerJob])

  const reset = useCallback(() => {
    generationRef.current += 1
    jobRef.current = null
    operationRef.current?.error('cancelled')
    operationRef.current = null
    trimLockRef.current = false
    workerJob.reset()
    revokeSource()
    revokeResult()
    setFile(null)
    setMetadata(null)
    setSnappedRange(null)
    setStartSeconds(0)
    setEndSeconds(0)
    setError(null)
    previewRejectedRef.current = false
    if (inputRef.current) inputRef.current.value = ''
  }, [workerJob])

  function revokeSource() {
    if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current)
    sourceUrlRef.current = null
    setSourceUrl(null)
  }

  function revokeResult() {
    if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current)
    resultUrlRef.current = null
    setResultUrl(null)
  }

  const busy = workerJob.state.status === 'running' || workerJob.state.status === 'validating'
  const copy = locale === 'ru' ? ru : en
  const duration = metadata?.durationSeconds ?? 0

  function rejectPreview() {
    if (previewRejectedRef.current || !file) return
    previewRejectedRef.current = true
    generationRef.current += 1
    const activeTrim = trimLockRef.current || jobRef.current?.kind === 'trim'
    jobRef.current = null
    workerJob.reset()
    if (activeTrim) operationRef.current?.error('unsupported_input')
    else trackToolRejection(analytics, 'unsupported_input')
    operationRef.current = null
    trimLockRef.current = false
    revokeResult()
    revokeSource()
    setFile(null)
    setMetadata(null)
    setSnappedRange(null)
    setStartSeconds(0)
    setEndSeconds(0)
    setError('unsupported_input')
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <Card className="overflow-hidden border-rose-200/80 bg-card/95 shadow-lg dark:border-rose-900/60" aria-busy={busy}>
      <CardContent className="space-y-4 p-4 sm:p-6">
        <div className="rounded-lg border border-dashed bg-background/70 p-4 text-center sm:p-5">
          <input
            ref={inputRef}
            type="file"
            accept=".mp3,.wav,.webm,audio/mpeg,audio/wav,audio/webm"
            className="sr-only"
            onChange={(event: ChangeEvent<HTMLInputElement>) => applyFile(event.target.files)}
            aria-label={copy.choose}
          />
          <Button className="min-h-11" onClick={() => inputRef.current?.click()} disabled={busy}>
            {file ? copy.change : copy.choose}
          </Button>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">{copy.limits}</p>
        </div>

        {file && sourceUrl && (
          <div className="space-y-3 rounded-lg border bg-background/70 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-medium">{file.name}</p>
                <p className="text-sm text-muted-foreground">{formatBytes(file.size, locale)}{metadata ? ` · ${metadata.container.toUpperCase()} · ${metadata.codec} · ${formatTime(metadata.durationSeconds)}` : ''}</p>
              </div>
              <Button className="min-h-11" variant="outline" onClick={reset}>{copy.reset}</Button>
            </div>
            <audio className="w-full" controls preload="metadata" src={sourceUrl} onError={rejectPreview} aria-label={copy.sourcePreview} />
          </div>
        )}

        {metadata && (
          <div className="grid gap-4 rounded-lg border bg-card p-4 sm:grid-cols-2">
            <TimeField label={copy.start} value={startSeconds} max={Math.max(0, endSeconds - 0.01)} onChange={setStartSeconds} />
            <TimeField label={copy.end} value={endSeconds} min={Math.min(duration, startSeconds + 0.01)} max={duration} onChange={setEndSeconds} />
            <p className="text-sm text-muted-foreground sm:col-span-2">{copy.boundary}</p>
          </div>
        )}

        {busy && (
          <div role="status" aria-live="polite" className="rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary">
            {jobRef.current?.kind === 'inspect' ? copy.inspecting : copy.processing}
          </div>
        )}
        {error && <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">{errorText(error, copy)}</div>}

        <div className="flex flex-wrap gap-3">
          {metadata && !busy && <Button className="min-h-11" onClick={startTrim}>{copy.trim}</Button>}
          {busy && jobRef.current?.kind === 'trim' && <Button className="min-h-11" variant="outline" onClick={cancel}>{copy.cancel}</Button>}
          {error === 'cancelled' && file && metadata && <Button className="min-h-11" variant="outline" onClick={startTrim}>{copy.retry}</Button>}
        </div>

        {resultUrl && metadata && (
          <div ref={resultRef} className="space-y-3 rounded-lg border border-emerald-300 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-950/30">
            <p className="font-medium text-emerald-950 dark:text-emerald-100">{copy.ready}</p>
            {snappedRange && <p className="text-sm text-emerald-900 dark:text-emerald-200">{copy.actual}: {formatTime(snappedRange.start)}–{formatTime(snappedRange.end)}</p>}
            <audio className="w-full" controls src={resultUrl} aria-label={copy.resultPreview} />
            <Button asChild className="min-h-11"><a href={resultUrl} download={downloadName(file?.name, metadata.extension)}>{copy.download}</a></Button>
          </div>
        )}

        <p className="text-sm font-medium text-muted-foreground">{copy.privacy}</p>
      </CardContent>
    </Card>
  )
}

function TimeField({ label, value, min = 0, max, onChange }: { label: string; value: number; min?: number; max: number; onChange: (value: number) => void }) {
  return <label className="block"><span className="text-sm font-medium">{label}</span><input type="number" inputMode="decimal" step="0.01" min={min} max={max} value={value} onChange={(event) => onChange(Number(event.target.value))} className="mt-2 min-h-11 w-full rounded-md border bg-background px-3 py-2 font-mono tabular-nums outline-none focus:ring-2 focus:ring-primary/30" /></label>
}

function isFailureCode(value: string): value is AudioTrimFailureCode {
  return value === 'invalid_input' || value === 'unsupported_input' || value === 'size_limit' || value === 'unsupported_output' || value === 'processing_failed'
}

function toFailureCode(error: WorkerErrorPayload): AudioTrimFailureCode {
  return isFailureCode(error.code) ? error.code : 'processing_failed'
}

function roundTime(value: number): number { return Math.round(value * 1000) / 1000 }
function formatTime(value: number): string { const seconds = Math.max(0, value); const minutes = Math.floor(seconds / 60); return `${String(minutes).padStart(2, '0')}:${(seconds % 60).toFixed(2).padStart(5, '0')}` }
function formatBytes(value: number, locale: 'ru' | 'en'): string {
  return `${(value / 1024 / 1024).toLocaleString(locale === 'ru' ? 'ru-RU' : 'en-US', { maximumFractionDigits: 1 })} ${locale === 'ru' ? 'МБ' : 'MB'}`
}
function downloadName(name: string | undefined, extension: string): string { const base = (name ?? 'audio').replace(/\.[^.]+$/, '').replace(/[^a-zA-Zа-яА-ЯёЁ0-9_-]+/g, '-').slice(0, 80) || 'audio'; return `${base}-trimmed.${extension}` }

interface Copy {
  choose: string; change: string; reset: string
  sourcePreview: string; resultPreview: string; limits: string; start: string; end: string
  boundary: string; inspecting: string; processing: string; trim: string; cancel: string
  retry: string; ready: string; actual: string; download: string; privacy: string
  invalid: string; unsupported: string; size: string; output: string; failed: string; cancelled: string
}

const ru: Copy = {
  choose: 'Выбрать аудиофайл', change: 'Выбрать другой файл', reset: 'Сбросить', sourcePreview: 'Исходное аудио', resultPreview: 'Обрезанное аудио',
  limits: 'MP3, PCM WAV и аудио WebM/Opus. Один файл до 25 МБ и 20 минут.', start: 'Начало, сек', end: 'Конец, сек',
  boundary: 'WAV выравнивается по PCM-блокам, MP3 — по независимо декодируемым кадрам, WebM/Opus — по пакетам. Фактический диапазон может расшириться до целых границ и будет показан.', inspecting: 'Проверяем контейнер, дорожки и кодек…',
  processing: 'Копируем аудиоданные локально без перекодирования…', trim: 'Обрезать', cancel: 'Отменить', retry: 'Повторить', ready: 'Фрагмент готов', actual: 'Фактические границы', download: 'Скачать фрагмент',
  privacy: 'Локально в браузере — файл не отправляется на сервер.', invalid: 'Проверьте границы фрагмента и выберите ровно один непустой файл.', unsupported: 'Файл не поддерживается: нужен один аудиотрек без видео, с совместимым контейнером и кодеком.',
  size: 'Превышен лимит 25 МБ или 20 минут.', output: 'Не удалось доказать сохранение исходного кодека и контейнера.', failed: 'Не удалось обработать файл. Выберите другой файл или повторите.', cancelled: 'Операция отменена; устаревший результат удалён.',
}
const en: Copy = {
  choose: 'Choose audio file', change: 'Choose another file', reset: 'Reset', sourcePreview: 'Source audio', resultPreview: 'Trimmed audio',
  limits: 'MP3, PCM WAV, and audio-only WebM/Opus. One file up to 25 MB and 20 minutes.', start: 'Start, sec', end: 'End, sec',
  boundary: 'WAV snaps to PCM blocks, MP3 to independently decodable frames, and WebM/Opus to packet boundaries. The actual range may expand to whole boundaries and will be shown.', inspecting: 'Checking container, tracks, and codec…',
  processing: 'Copying audio data locally without transcoding…', trim: 'Trim', cancel: 'Cancel', retry: 'Retry', ready: 'Fragment ready', actual: 'Actual boundaries', download: 'Download fragment',
  privacy: 'Local in your browser — the file is never sent to a server.', invalid: 'Check the range and choose exactly one non-empty file.', unsupported: 'Unsupported file: exactly one audio track, no video, and a compatible container and codec are required.',
  size: 'The 25 MB or 20 minute limit was exceeded.', output: 'Could not prove that the original codec and container were preserved.', failed: 'Could not process the file. Choose another file or retry.', cancelled: 'The operation was cancelled and stale output was removed.',
}

function errorText(error: AudioTrimFailureCode | 'cancelled', copy: Copy): string {
  if (error === 'invalid_input') return copy.invalid
  if (error === 'unsupported_input') return copy.unsupported
  if (error === 'size_limit') return copy.size
  if (error === 'unsupported_output') return copy.output
  if (error === 'cancelled') return copy.cancelled
  return copy.failed
}
